import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "src/components/Button";
import ImageDropZone from "src/components/ImageDropZone";
import Input from "src/components/Input";
import SideModal from "src/components/Modal/SideModal";
import handleError from "src/helpers/errorHandler";
import sendRequest from "src/helpers/service";
import { LiveStage } from "src/types/live";

interface StageModalProps {
  live_id: number;
  stage: "new" | LiveStage;
  onClose: () => void;
  onSave?: () => void;
  onUpdate?: () => void;
}

export default function StageModal({ live_id, stage, onClose, onSave, onUpdate }: StageModalProps) {
  const isEdit = stage !== "new";

  const [attempted, setAttempted] = useState(false);
  const [title, setTitle] = useState("");
  const [thumbnail, setThumbnail] = useState<File | string>();
  const [channelId, setChannelId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit && stage) {
      setTitle(stage.title || "");
      setChannelId(stage.channel_id || "");
      setThumbnail(stage.thumbnail);
    }else {
      setChannelId(`live_${live_id}_${Date.now()}`);
    }
  }, []);

  const handleSubmit = async () => {
    setAttempted(true);

    if (!title) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("agora_channel", channelId);

      if (thumbnail && typeof thumbnail !== "string") {
        formData.append("thumbnail", thumbnail);
      }

      if (stage === "new") {
        formData.append("live_id", live_id + "");
        await sendRequest.post({
          url: "live/stage/save",
          data: formData,
        });
        toast("Stage created successfully", {
          theme: "colored",
          type: "success",
        });
        onSave();
      } else {
        await sendRequest.post({
          url: `live/stage/update/${stage.id}`,
          data: formData,
        });
        toast("Stage updated successfully", {
          theme: "colored",
          type: "success",
        });
        onUpdate();
      }

      onClose();
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  };

  return (
    <SideModal title={isEdit ? "Edit Stage" : "Add New Stage"} onClose={onClose}>
      <DialogContent>
        <Grid
          container
          spacing={2}
          mt={1}
        >
          <Grid
            item
            xs={12}
          >
            <Input
              label="Stage Name"
              variant="outlined"
              value={title}
              onChange={setTitle}
              mandatory
              warn={attempted && !title}
              disabled={loading}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Input
              label="Agora Channel ID"
              variant="outlined"
              value={channelId}
              onChange={setChannelId}
              subLabel="Used for Agora live streaming SDK"
              disabled
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <ImageDropZone
              value={thumbnail}
              label="Stage Thumbnail"
              onChange={setThumbnail}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          theme="secondary"
          loader={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="success"
          loader={loading}
        >
          {isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </SideModal>
  );
}
